import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import { App } from './App.tsx'
import { scrubAuthActionParams } from './utils/authActionUrl'

/*
  A Firebase action link carries its code in the query string (a reset code is an account takeover
  for the hour it lives). The action page drops it from the address bar once read, but the page-load
  URL is captured before that, so every URL Sentry records is scrubbed as well.
*/
const scrubEventUrls = <T extends Sentry.Event>(event: T): T => {
  if (event.request?.url) {
    event.request.url = scrubAuthActionParams(event.request.url)
  }
  event.breadcrumbs?.forEach(scrubBreadcrumbUrls)
  return event
}

const scrubBreadcrumbUrls = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  if (breadcrumb.data) {
    for (const key of ['url', 'from', 'to']) {
      if (key in breadcrumb.data) {
        breadcrumb.data[key] = scrubAuthActionParams(breadcrumb.data[key])
      }
    }
  }
  return breadcrumb
}

// Initialize Sentry before app renders
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.PROD ? 'production' : 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      /*
        The invitation token is the whole credential: it admits its bearer at whatever role the
        invitation carries, up to and including admin, while the real invitee sees only the generic
        "invalid or has expired".

        Replay captures request and response bodies only for URLs listed in
        `networkDetailAllowUrls`, which is empty, so nothing is captured today. This entry is here
        because the token recently moved out of the URL and into a POST body: the old shape was
        captured by anything that logged a request path, and the new one is one allow-list entry
        away from being handed to a third party instead. Deny wins over allow, so naming the prefix
        here means adding this path to an allow list later cannot quietly re-open it.

        Scope, so nobody reads more into this than it does: these two lists gate request and
        response BODIES and HEADERS only. Replay still records every request's URL, method and
        status as a breadcrumb whatever they say. That is fine while the token travels in the body,
        but it means this entry would NOT protect a token that moved back into a path or a query
        string — the only thing that protects that is keeping it out of the URL.

        A string rather than a regex: Replay treats a string as "contains", which covers both
        /api/public/invitations/preview and /accept, and leaves nothing to escape.
      */
      networkDetailDenyUrls: ['/api/public/invitations/'],
    }),
  ],
  beforeBreadcrumb: scrubBreadcrumbUrls,
  beforeSend: scrubEventUrls,
  beforeSendTransaction: scrubEventUrls,
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
